import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, ChevronDown, Boxes, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { groupByName } from "../lib/variants";
import { GOALS } from "../lib/goals";
import { useCart } from "../context/CartContext";
import { Seo, siteOrigin } from "../components/Seo";
import { ProductCard } from "../components/ProductCard";
import { GoalTile } from "../components/GoalTile";
import { TrustStrip } from "../components/TrustStrip";
import { TestimonialStrip } from "../components/TestimonialStrip";

function SectionHeading({ icon: Icon, title, action, open, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="w-full flex items-center gap-3 mb-6 sm:mb-10 text-left lg:pointer-events-none"
      aria-expanded={open}
    >
      {Icon && <Icon className="w-5 h-5 text-[#8C7B6B] fill-[#8C7B6B] shrink-0" />}
      <h2 className="font-display text-base sm:text-xl uppercase tracking-wide text-[#1A1A1A]">{title}</h2>
      <div className="flex-1 h-px bg-[#DDD7CF]" />
      {action}
      <ChevronDown className={`w-5 h-5 text-[#8C7B6B] shrink-0 lg:hidden transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

export default function Home() {
  const [rawProducts, setRawProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestOpen, setBestOpen] = useState(true);
  const [bundlesVisible, setBundlesVisible] = useState(false);
  const [maxBundleSavings, setMaxBundleSavings] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setRawProducts(data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
    api.get("/settings")
      .then(({ data }) => setBundlesVisible(!!data.bundles_visible))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!bundlesVisible) return;
    api.get("/bundles")
      .then(({ data }) => setMaxBundleSavings(Math.max(0, ...data.map((b) => b.savings || 0), 0)))
      .catch(() => {});
  }, [bundlesVisible]);

  const families = useMemo(() => groupByName(rawProducts), [rawProducts]);
  const bestSellers = useMemo(() => families.filter((p) => p.featured), [families]);
  const goals = useMemo(() => GOALS.filter((g) => families.some((f) => f.category === g.category)), [families]);

  const handleAdd = (e, product) => {
    e.preventDefault();
    addItem(product);
    toast.success(`${product.name} ${product.dosage} added to cart`);
  };

  return (
    <div className="relative z-10">
      <Seo
        path="/"
        description="Premium research-grade peptides in Canada — BPC-157, TB-500, Retatrutide, NAD+ and more. Third-party lab tested with transparent COAs. Pay by Interac e-Transfer, fast Canadian shipping."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", name: "Eternal Biolabs", url: siteOrigin(), logo: `${siteOrigin()}/eb-logo.png` },
            { "@type": "WebSite", name: "Eternal Biolabs", url: siteOrigin() },
            {
              "@type": "OnlineStore", name: "Eternal Biolabs", url: siteOrigin(),
              description: "Canadian supplier of research-grade peptides with third-party certificates of analysis.",
              currenciesAccepted: "CAD", paymentAccepted: "Interac e-Transfer, Credit Card",
              areaServed: { "@type": "Country", name: "Canada" },
            },
            {
              "@type": "ItemList", name: "Shop by research goal",
              itemListElement: GOALS.map((g, i) => ({ "@type": "ListItem", position: i + 1, name: g.name, url: `${siteOrigin()}/shop/${g.slug}` })),
            },
          ],
        }}
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 sm:pt-20 pb-10 sm:pb-14">
        <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-5 animate-fade-in">
          Research-Grade · Third-Party Tested · Canada
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide uppercase leading-tight text-[#1A1A1A] max-w-3xl animate-fade-in" data-testid="hero-heading">
          Premium peptides,<br />
          <span className="text-[#8C7B6B]">engineered</span> to endure.
        </h1>
        <p className="text-base leading-relaxed text-[#6E675F] max-w-xl mt-6 animate-fade-in">
          Browse by research goal, compare vial sizes, and read the citation-backed guide for every compound before you order.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-8 animate-fade-in">
          <a href="#goals" data-testid="hero-shop-button"
            className="inline-flex items-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#75665A] hover:-translate-y-0.5 transition-transform duration-300">
            Shop by goal <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/shop" data-testid="hero-shop-all-link"
            className="inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-300">
            Shop all {families.length > 0 && <span className="text-[#8C7B6B] group-hover:text-inherit">· {families.length}</span>}
          </Link>
        </div>
      </section>

      <TrustStrip />

      <section id="goals" className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-6 scroll-mt-24" data-testid="shop-by-goal-section">
        <div className="flex items-end justify-between gap-4 mb-6 sm:mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-2">Start here</p>
            <h2 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]">Shop by research goal</h2>
          </div>
          <Link to="/shop" data-testid="goals-shop-all-link" className="hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A] transition-colors shrink-0">
            View all products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="goal-tile-grid">
            {goals.map((g, i) => <GoalTile key={g.slug} goal={g} families={families} index={i} />)}
          </div>
        )}
      </section>

      {bestSellers.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-12 sm:pb-16" data-testid="best-sellers-section">
          <SectionHeading icon={Star} title="Best Sellers" open={bestOpen} onToggle={() => setBestOpen((v) => !v)} testid="best-sellers-toggle" />
          <div className={`${bestOpen ? "grid" : "hidden"} lg:grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-8 sm:gap-y-14`} data-testid="best-sellers-grid">
            {bestSellers.slice(0, 8).map((p, i) => <ProductCard key={p.id} p={p} i={i} onAdd={handleAdd} />)}
          </div>
        </section>
      )}

      <TestimonialStrip className="pb-16" />

      {bundlesVisible && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16" data-testid="bundles-banner">
          <Link
            to="/bundles"
            data-testid="bundles-banner-link"
            className="relative overflow-hidden rounded-2xl border border-[#DDD7CF] bg-[#F3EFE9] px-6 py-7 sm:px-10 flex items-center gap-5 sm:gap-8 group hover:border-[#8C7B6B] transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-white border border-[#D4B36A]/40 flex items-center justify-center shrink-0">
              <Boxes className="w-6 h-6 text-[#8C7B6B]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-base sm:text-xl uppercase tracking-wide text-[#1A1A1A]">Bundles</h2>
                {maxBundleSavings > 0 && (
                  <span data-testid="bundles-banner-savings" className="inline-flex items-center gap-1 bg-[#2D6A4F] text-white text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1 rounded-none">
                    <Tag className="w-3 h-3" /> Save up to ${maxBundleSavings.toFixed(2)}
                  </span>
                )}
              </div>
              <p className="text-sm text-[#6E675F] mt-1.5">
                Curated peptide stacks for recovery, longevity and performance — at exclusive bundle pricing.
              </p>
            </div>
            <span className="ml-auto hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#8C7B6B] group-hover:translate-x-1 transition-transform">
              Shop Stacks <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </section>
      )}
    </div>
  );
}
